import React from 'react'
import { cn } from '../atoms'

export type IndicatorTone = 'blue' | 'slate' | 'red' | 'amber' | 'green' | 'purple'

export interface IndicatorCardProps {
  label: string
  value: number | string
  icon: React.ComponentType<{ className?: string }>
  tone: IndicatorTone
  active?: boolean
  onClick?: () => void
  detail?: string
  className?: string
}

const TONES: Record<IndicatorTone, string> = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  slate: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  red: 'bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-400',
  amber: 'bg-amber-50 text-amber-500 dark:bg-amber-900/30 dark:text-amber-400',
  green: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400',
  purple: 'bg-purple-50 text-purple-500 dark:bg-purple-900/30 dark:text-purple-400',
}

export function IndicatorCard({
  label,
  value,
  icon: Icon,
  tone,
  active = false,
  onClick,
  detail,
  className,
}: IndicatorCardProps) {
  const formattedValue = typeof value === 'number' ? value.toLocaleString('es-CO') : value

  const Content = (
    <div className="flex items-center gap-2.5 w-full">
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', TONES[tone])}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-lg font-bold leading-tight text-slate-900 dark:text-gray-100">{formattedValue}</p>
        <p className={cn('truncate text-[10px]', active ? 'font-semibold text-primary-700 dark:text-primary-300' : 'text-slate-500')}>{label}</p>
        {detail && <p className="truncate text-[9px] text-slate-400 dark:text-gray-500">{detail}</p>}
      </div>
    </div>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'group rounded-xl border p-3 text-left shadow-sm transition-all cursor-pointer w-full',
          active
            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/40 dark:bg-primary-950/40 shadow-md'
            : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md dark:bg-gray-900 dark:border-gray-700 dark:hover:border-primary-700',
          className
        )}
      >
        {Content}
      </button>
    )
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm dark:bg-gray-900 dark:border-gray-700 w-full',
        className
      )}
    >
      {Content}
    </div>
  )
}
